import React, { useState } from "react";
import "./WebDevelopment.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCode,
  faCloud,
  faGaugeHigh,
  faRobot,
  faTableColumns,
  faArrowsRotate,
  faLifeRing,
} from "@fortawesome/free-solid-svg-icons";
import { faWordpress } from "@fortawesome/free-brands-svg-icons";

import ServiceContactModal from "../ServiceContactModal/ServiceContactModal"; // adjust path

const devServices = [
  {
    title: "Custom Web Application Development",
    tag: "Custom",
    icon: faCode,
    desc: "Bespoke web apps built around your exact workflows, not generic templates or one-size-fits-all tooling.",
  },
  {
    title: "WordPress & Headless CMS Builds",
    tag: "WordPress",
    icon: faWordpress,
    desc: "High-performance WordPress sites, custom themes and blocks, plus headless builds with modern frontends.",
  },
  {
    title: "SaaS & MVP Development",
    tag: "SaaS / MVP",
    icon: faCloud,
    desc: "Launch-ready SaaS and MVP products with subscription billing, auth, dashboards and onboarding built in.",
  },
  {
    title: "Automated Dashboards & Internal Tools",
    tag: "Dashboards",
    icon: faTableColumns,
    desc: "Ops dashboards, admin consoles and internal tools that give teams a clear, real-time view of the business.",
  },
  {
    title: "AI-Powered Features & Assistants",
    tag: "AI",
    icon: faRobot,
    desc: "Chatbots, AI copilots and smart automations built on OpenAI and other LLMs, wired into your own data.",
  },
  {
    title: "Performance & Core Web Vitals",
    tag: "Speed",
    icon: faGaugeHigh,
    desc: "Optimisation for speed, accessibility and SEO so your product feels fast and ranks well from day one.",
  },
  {
    title: "Workflow Automation & Integrations",
    tag: "Automation",
    icon: faArrowsRotate,
    desc: "Secure APIs and no-code / low-code automations that connect your CRM, billing, support and marketing stack.",
  },
  {
    title: "Ongoing Product Support & Iteration",
    tag: "Support",
    icon: faLifeRing,
    desc: "Retainers for roadmap, new features and tech support so your product keeps improving after launch.",
  },
];

const processSteps = [
  {
    title: "1. Discovery & Architecture",
    desc: "We map goals, users and constraints, then propose the right stack and product architecture for your stage.",
  },
  {
    title: "2. UX, UI & Technical Design",
    desc: "Wireframes, user flows and a clear technical plan so everyone understands what we’re building and why.",
  },
  {
    title: "3. Build, Integrate & Test",
    desc: "Agile sprints, clean code, integrations and QA — with regular demos so you can see progress, not guess.",
  },
  {
    title: "4. Launch & Ongoing Improvements",
    desc: "We handle deployment, monitoring and continuous improvements based on real user feedback and metrics.",
  },
];

const techStack = [
  "React / Next.js",
  "Node.js / Express",
  "Laravel / PHP",
  "WordPress / WooCommerce",
  "Tailwind / modern CSS",
  "PostgreSQL / MySQL",
  "Firebase / Supabase",
  "AWS / Vercel / Render",
  "OpenAI & LLM APIs",
  "Zapier / Make / n8n",
];

const WebDevelopment = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(
    "Web & Product Development"
  );

  const openModal = (service = "Web & Product Development") => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <section className="wd-section">
        <div className="wd-inner">
          {/* Hero / intro */}
          <header className="wd-header">
            <p className="wd-eyebrow">WEB & PRODUCT DEVELOPMENT</p>
            <h1 className="wd-title">
              Custom Web, WordPress, SaaS & AI Development for Growing Teams
            </h1>
            <p className="wd-sub">
              We design, build and ship digital products that feel fast, modern
              and reliable — from marketing sites and WordPress builds to full
              SaaS platforms, AI assistants and automated internal tools.
            </p>

            <div className="wd-pill-row">
              <span className="wd-pill">Custom app builds</span>
              <span className="wd-pill">WordPress & SaaS MVPs</span>
              <span className="wd-pill">Dashboards & AI features</span>
            </div>

            <div className="wd-cta-row">
              <button
                type="button"
                className="wd-cta-primary"
                onClick={() => openModal("Web & Product Development")}
              >
                Plan a build with us
              </button>
              <p className="wd-cta-note">
                Share where you are today and we’ll outline a realistic roadmap,
                stack and timeline — no fluff.
              </p>
            </div>
          </header>

          {/* Services grid */}
          <section className="wd-block">
            <div className="wd-block-header">
              <h2 className="wd-section-title">
                Development services we can plug into your roadmap
              </h2>
              <p className="wd-section-sub">
                Pick a single project or combine services into a staged roadmap
                — starting with MVPs and landing pages, evolving into full
                platforms and ongoing product support.
              </p>
            </div>

            <div className="wd-grid">
              {devServices.map((service, idx) => (
                <article key={idx} className="wd-card">
                  <div className="wd-card-top">
                    <div className="wd-icon">
                      <FontAwesomeIcon icon={service.icon} />
                    </div>
                    <span className="wd-tag">{service.tag}</span>
                  </div>
                  <h3 className="wd-card-title">{service.title}</h3>
                  <p className="wd-card-desc">{service.desc}</p>

                  <button
                    type="button"
                    className="wd-card-btn"
                    onClick={() => openModal(service.title)}
                  >
                    Discuss this service
                  </button>
                </article>
              ))}
            </div>
          </section>

          {/* Process + tech stack */}
          <section className="wd-process-row">
            <div className="wd-process">
              <h2 className="wd-section-title">
                How we run development projects
              </h2>
              <p className="wd-section-sub">
                A simple, transparent flow from first idea to launch — with
                checkpoints where we refine based on your feedback and live
                data.
              </p>

              <div className="wd-process-grid">
                {processSteps.map((step, idx) => (
                  <div key={idx} className="wd-process-card">
                    <p className="wd-process-step">{step.title}</p>
                    <p className="wd-process-desc">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="wd-tech">
              <h2 className="wd-section-title">
                Tech stack we love working with
              </h2>
              <p className="wd-section-sub">
                We favour modern, battle-tested tools that are easy to maintain,
                scale and hand over to in-house teams whenever you're ready.
              </p>
              <div className="wd-tech-row">
                {techStack.map((item, idx) => (
                  <span key={idx} className="wd-tech-chip">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* Modal */}
      <ServiceContactModal
        isOpen={isModalOpen}
        onClose={closeModal}
        defaultService={selectedService}
      />
    </>
  );
};

export default WebDevelopment;
